import React from 'react';
import Routes from 'Routes';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectAllBoothTiers } from 'models/cms/boothTiers/selectors';
import { nameByLocale } from 'hooks/useNameByLocale';
import GenericSearchResultItem from './GenericSearchResultItem';
import WebinarSearchResultItem from './WebinarSearchResultItem';
import ParticipantSearchResultItem from './ParticipantSearchResultItem';
import SpeakerSearchResultItem from './SpeakerSearchResultItem';

const pickFromExhibitor = (exhibitor, allTiers, locale) => {
  const target = exhibitor.appliedDesignCodename === '002'
    ? 'middle_banner'
    : 'top_banner';

  const logo = exhibitor.logo?.values?.filter((i) => i.locale === locale)[0]?.value?.originalImageUrl
    || (exhibitor.designElements || [])
      .find((node) => node.name === target)
      ?.image?.[locale]?.originalImageUrl;

  const tierObj = allTiers.find((tier) => tier?.id === exhibitor?.tier?.id);

  const boothName = exhibitor.name?.values?.filter((i) => i.locale === locale)?.[0]?.value;
  return {
    name: boothName || null,
    logo,
    description: '',
    tier: (tierObj?.nameVisible ? tierObj?.name?.[locale] : null),
  };
};

const SearchResultList = ({ type, results }) => {
  const locale = useSelector(selectLocale);
  const allTiers = useSelector(selectAllBoothTiers);
  const { features } = useSelector((s) => s.eventConfigs);

  switch (type) {
    case 'exhibitor': {
      return results.map((exhibitor) => {
        const {
          name,
          logo,
          description,
          tier,
        } = pickFromExhibitor(exhibitor, features.includes('boothTiers') ? allTiers : [], locale);

        return name ? (
          <GenericSearchResultItem
            key={exhibitor.id}
            id={`${exhibitor.id}`}
            name={name}
            to={Routes.booth(exhibitor.id)}
            logo={logo}
            tier={tier}
            description={description}
          />
        ) : null;
      });
    }
    case 'webinar': {
      return results.map(({
        id,
        kind,
        startAt,
        endAt,
        translations,
        timezone,
      }) => {
        const { title } = translations[locale] || {};

        return (
          <WebinarSearchResultItem
            key={id}
            type={kind}
            id={`${id}`}
            name={title}
            startAt={startAt}
            endAt={endAt}
            timezone={timezone}
          />
        );
      });
    }
    case 'participant': {
      return results.map((participant) => {
        const {
          firstName,
          lastName,
          name,
          uuid,
          customAttendeeFields: {
            vexpo_chat_tag: fallbackTag,
            vexpo_chat_user_description: fallbackDescription,
          },
          vexpoChatTag,
          vexpoChatUserDescription,
        } = participant;
        return (
          <ParticipantSearchResultItem
            key={uuid}
            uuid={uuid}
            name={nameByLocale(firstName, lastName, locale, name)}
            userTag={vexpoChatTag || fallbackTag}
            userDescription={vexpoChatUserDescription || fallbackDescription}
          />
        );
      });
    }
    case 'products': {
      return results.map((product) => {
        const { name, image } = product;
        return (
          <GenericSearchResultItem
            key={product.id}
            id={`${product.id}`}
            to={Routes.productDetail(product.id)}
            logo={findAvailableLocale(image, locale)}
            name={findAvailableLocale(name, locale)}
          />
        );
      });
    }
    case 'speakers': {
      return results.map(({
        id,
        webinarId,
        translations,
        // eslint-disable-next-line camelcase
        profile_picture_url,
      }) => {
        const { name, company, title } = translations[locale] || {};
        return (
          <SpeakerSearchResultItem
            key={id}
            // eslint-disable-next-line camelcase
            picture={profile_picture_url}
            id={`${id}`}
            webinarId={`${webinarId}`}
            name={name}
            company={company}
            title={title}
          />
        );
      });
    }
    default:
      return null;
  }
};

SearchResultList.propTypes = {
  type: PropTypes.oneOf([
    'exhibitor',
    'webinar',
    'participant',
    'products',
    'speakers',
  ]),
  results: PropTypes.arrayOf(PropTypes.shape()),
};

SearchResultList.defaultProps = {
  type: 'exhibitor',
  results: [],
};

export default SearchResultList;
